import React from 'react'
import { reportError } from 'util/error-reporting'

interface ErrorBoundaryProps {
	/** Where in the tree this boundary sits — shown in reports, not to users */
	label: string
	children: React.ReactNode
}

interface ErrorBoundaryState {
	hasError: boolean
}

/**
 * Catches render/lifecycle errors below it so one broken component can't
 * white-screen the whole app mid-class (M-BIG3 / err-3, err-13). Shows a
 * kid-friendly fallback with a reload button and reports the error.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
	state: ErrorBoundaryState = { hasError: false }

	static getDerivedStateFromError(): ErrorBoundaryState {
		return { hasError: true }
	}

	componentDidCatch(error: Error, info: React.ErrorInfo) {
		reportError('react-boundary', error, {
			boundary: this.props.label,
			componentStack: String(info.componentStack ?? '').slice(0, 1000),
		})
	}

	render() {
		if (!this.state.hasError) return this.props.children
		return (
			<div style={{
				display: 'flex', flexDirection: 'column', alignItems: 'center',
				justifyContent: 'center', minHeight: '60vh', gap: '16px',
				fontFamily: 'inherit', textAlign: 'center', padding: '24px',
			}}>
				<span style={{ fontSize: '28px', fontWeight: 700, color: 'var(--blue, #263b9f)' }}>
					Oops! Something went wrong.
				</span>
				<span style={{ fontSize: '18px', color: '#333' }}>
					Don't worry — press the button below to get back on track.
				</span>
				<button
					onClick={() => window.location.reload()}
					style={{
						fontSize: '18px', fontWeight: 600, color: '#fff',
						background: 'var(--blue, #263b9f)', border: 'none', borderRadius: '8px',
						padding: '12px 28px', cursor: 'pointer',
					}}
				>
					Reload
				</button>
			</div>
		)
	}
}
